import React from 'react';
import { CustomAlert } from 'components/AlertsCollection';
import { ConfirmationDialogLabels } from 'components/ConfirmationDialogBox';
import { StudyActionTarget } from 'components/Flux/shared/useStudyActions';
import { myStudyActionErrorMessage } from 'helper/flux/myStudyActionErrors';
import { ResendStudyResult } from 'helper/personalized';
import { useResendPersonalizedStudyMutation } from 'services/api/personalized';
import useConfirmationDialogBox from 'utils/useConfirmationDialogBox';

// D12: feedback de sucesso do Reenviar na tela nova
export const RESEND_SUCCESS_MESSAGE = 'Estudo reenviado para a fila.';
export const RESEND_REFUSED_MESSAGE = 'O Flux não aceitou o reenvio. Tente novamente em instantes.';
export const RESEND_ERROR_MESSAGE = 'Não foi possível reenviar o estudo. Tente novamente em instantes.';

export interface ResendStudyOptions {
  /** Título e botões do diálogo. */
  labels?: ConfirmationDialogLabels;
  /** Recebe a mensagem de resultado (sucesso, aviso ou erro). */
  onFeedback: (feedback: CustomAlert) => void;
  /** Chamado quando o Flux aceita o reenvio. */
  onSuccess?: () => void;
  /**
   * Texto do erro, ou `null` quando o erro já foi tratado (ex.: sessão expirada fez logout).
   * Sem ele, vale `myStudyActionErrorMessage`.
   */
  errorMessage?: (error: unknown) => string | null;
  /** Mutation de reenvio do produto; padrão: a do Personalizado (o Upload passa a sua). */
  useResendMutation?: typeof useResendPersonalizedStudyMutation;
}

/**
 * Reenviar um estudo do Flux Personalizado, com confirmação (#1994). A resposta do re-execute
 * não traz `code`; a mutation `resendPersonalizedStudy` lê o `success`, e erro nunca leva a
 * `/error`: vira mensagem para a tela.
 */
export default function useResendStudy(
  study: StudyActionTarget,
  {
    labels, onFeedback, onSuccess, errorMessage, useResendMutation: useProductResendMutation,
  }: ResendStudyOptions,
) {
  // O hook escolhido não muda entre renders do mesmo produto, então a ordem dos hooks é estável
  const useResendMutation = useProductResendMutation ?? useResendPersonalizedStudyMutation;
  const [triggerResend, { isLoading: resending }] = useResendMutation();
  const { confirmationDialogBox, setConfirmationDialogBox } = useConfirmationDialogBox();

  const resendStudy = async () => {
    let result: ResendStudyResult;
    try {
      result = await triggerResend(study.id).unwrap();
    } catch (error) {
      const message = errorMessage
        ? errorMessage(error)
        : myStudyActionErrorMessage(error, RESEND_ERROR_MESSAGE);
      if (message !== null) onFeedback({ title: '', message, variant: 'danger' });
      return;
    }
    if (!result.success) {
      onFeedback({ title: '', message: RESEND_REFUSED_MESSAGE, variant: 'warning' });
      return;
    }
    onFeedback({ title: '', message: RESEND_SUCCESS_MESSAGE, variant: 'success' });
    // Fora do try: um erro no callback da tela não vira "falha ao reenviar"
    onSuccess?.();
  };

  const confirmResend = () => setConfirmationDialogBox(
    true,
    (
      <span>
        <span className="fw-bold me-1">{study.ds_nome_estudo}</span>
        voltará para a fila de processamento.
      </span>
    ),
    resendStudy,
    undefined,
    labels,
  );

  return { confirmationDialogBox, confirmResend, resending };
}
